/**
 * Lighting nodes — point lights, directional lights, spot lights.
 */
import type { NodeBehaviour } from '@engine/redprint';
import { flowIn, flowOut, input, output } from '@engine/redprint';

// ============================================================================
// LIGHT COLOR & INTENSITY
// ============================================================================

export const SetLightColor: NodeBehaviour = {
  id: 'lighting.setColor',
  name: 'Set Light Color',
  category: 'Lighting',
  color: '#f1c40f',
  icon: 'Sun',
  description: 'Sets the color of a light.',
  keywords: ['light', 'color', 'hue', 'tint'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('color', 'Color', 'color', 0xFFFFFFFF),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setLightColor', [ctx.resolveInput('entity'), ctx.resolveInput('color')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setLightColor', eid, ctx.getInput('color'));
  },
};

export const SetLightIntensity: NodeBehaviour = {
  id: 'lighting.setIntensity',
  name: 'Set Light Intensity',
  category: 'Lighting',
  color: '#f1c40f',
  icon: 'Sun',
  description: 'Sets the intensity (brightness) of a light.',
  keywords: ['light', 'brightness', 'power'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('intensity', 'Intensity', 'f32', 1),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setLightIntensity', [ctx.resolveInput('entity'), ctx.resolveInput('intensity')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setLightIntensity', eid, ctx.getInput<number>('intensity'));
  },
};

export const GetLightIntensity: NodeBehaviour = {
  id: 'lighting.getIntensity',
  name: 'Get Light Intensity',
  category: 'Lighting',
  isPure: true,
  color: '#f1c40f',
  icon: 'Sun',
  description: 'Gets the current intensity of a light.',
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    output('intensity', 'Intensity', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getLightIntensity', [ctx.resolveInput('entity')], 'f32');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.setOutput('intensity', ctx.bridge.call('bridge:getLightIntensity', eid) as number ?? 1);
  },
};

// ============================================================================
// POINT LIGHT
// ============================================================================

export const SetPointLightRadius: NodeBehaviour = {
  id: 'lighting.setPointRadius',
  name: 'Set Point Light Radius',
  category: 'Lighting',
  color: '#f1c40f',
  icon: 'Circle',
  description: 'Sets the radius of a point light.',
  keywords: ['light', 'range', 'distance'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('radius', 'Radius', 'f32', 10),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setPointLightRadius', [ctx.resolveInput('entity'), ctx.resolveInput('radius')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setPointLightRadius', eid, ctx.getInput<number>('radius'));
  },
};

export const SetPointLightFalloff: NodeBehaviour = {
  id: 'lighting.setPointFalloff',
  name: 'Set Point Light Falloff',
  category: 'Lighting',
  color: '#f1c40f',
  icon: 'Circle',
  description: 'Sets the falloff curve of a point light.',
  keywords: ['light', 'attenuation', 'fade'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('falloff', 'Falloff', 'f32', 1),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setPointLightFalloff', [ctx.resolveInput('entity'), ctx.resolveInput('falloff')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setPointLightFalloff', eid, ctx.getInput<number>('falloff'));
  },
};

// ============================================================================
// SPOT LIGHT
// ============================================================================

export const SetSpotLightAngle: NodeBehaviour = {
  id: 'lighting.setSpotAngle',
  name: 'Set Spot Light Angle',
  category: 'Lighting',
  color: '#f1c40f',
  icon: 'Triangle',
  description: 'Sets the cone angle of a spot light (in degrees).',
  keywords: ['light', 'cone', 'spread'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('inner', 'Inner Angle', 'f32', 30),
    input('outer', 'Outer Angle', 'f32', 45),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setSpotLightAngle', [ctx.resolveInput('entity'), ctx.resolveInput('inner'), ctx.resolveInput('outer')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setSpotLightAngle', eid, ctx.getInput<number>('inner'), ctx.getInput<number>('outer'));
  },
};

export const SetSpotLightRange: NodeBehaviour = {
  id: 'lighting.setSpotRange',
  name: 'Set Spot Light Range',
  category: 'Lighting',
  color: '#f1c40f',
  icon: 'Triangle',
  description: 'Sets the range of a spot light.',
  keywords: ['light', 'distance', 'reach'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('range', 'Range', 'f32', 20),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setSpotLightRange', [ctx.resolveInput('entity'), ctx.resolveInput('range')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setSpotLightRange', eid, ctx.getInput<number>('range'));
  },
};

// ============================================================================
// DIRECTIONAL LIGHT
// ============================================================================

export const SetDirectionalLightDirection: NodeBehaviour = {
  id: 'lighting.setDirDirection',
  name: 'Set Directional Light Direction',
  category: 'Lighting',
  color: '#f1c40f',
  icon: 'SunDim',
  description: 'Sets the direction of a directional light.',
  keywords: ['light', 'sun', 'angle'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('x', 'Direction X', 'f32', 0),
    input('y', 'Direction Y', 'f32', -1),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setDirectionalLightDir', [ctx.resolveInput('entity'), ctx.resolveInput('x'), ctx.resolveInput('y')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setDirectionalLightDir', eid, ctx.getInput<number>('x'), ctx.getInput<number>('y'));
  },
};

// ============================================================================
// GENERAL LIGHT CONTROLS
// ============================================================================

export const EnableLight: NodeBehaviour = {
  id: 'lighting.enable',
  name: 'Enable Light',
  category: 'Lighting',
  color: '#f1c40f',
  icon: 'Lightbulb',
  description: 'Enables or disables a light.',
  keywords: ['light', 'on', 'off', 'toggle'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('enabled', 'Enabled', 'bool', true),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:enableLight', [ctx.resolveInput('entity'), ctx.resolveInput('enabled')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:enableLight', eid, ctx.getInput<boolean>('enabled'));
  },
};

export const IsLightEnabled: NodeBehaviour = {
  id: 'lighting.isEnabled',
  name: 'Is Light Enabled',
  category: 'Lighting',
  isPure: true,
  color: '#f1c40f',
  icon: 'Lightbulb',
  description: 'Checks if a light is enabled.',
  inputs: [
    input('entity', 'Entity', 'entity', 0),
  ],
  outputs: [
    output('enabled', 'Is Enabled', 'bool'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:isLightEnabled', [ctx.resolveInput('entity')], 'bool');
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.setOutput('enabled', ctx.bridge.call('bridge:isLightEnabled', eid) as boolean ?? false);
  },
};

export const SetLightCastShadows: NodeBehaviour = {
  id: 'lighting.setCastShadows',
  name: 'Set Cast Shadows',
  category: 'Lighting',
  color: '#f1c40f',
  icon: 'Eclipse',
  description: 'Enables or disables shadow casting for a light.',
  keywords: ['light', 'shadow', 'cast'],
  inputs: [
    flowIn(),
    input('entity', 'Entity', 'entity', 0),
    input('cast', 'Cast Shadows', 'bool', true),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setLightCastShadows', [ctx.resolveInput('entity'), ctx.resolveInput('cast')], 'void', true);
  },
  execute(ctx) {
    const eid = ctx.getInput<number>('entity') || ctx.entityId;
    ctx.bridge.call('bridge:setLightCastShadows', eid, ctx.getInput<boolean>('cast'));
  },
};

// ============================================================================
// AMBIENT LIGHT
// ============================================================================

export const SetAmbientLight: NodeBehaviour = {
  id: 'lighting.setAmbient',
  name: 'Set Ambient Light',
  category: 'Lighting',
  color: '#f1c40f',
  icon: 'Cloud',
  description: 'Sets the global ambient light color and intensity.',
  keywords: ['global', 'fill', 'environment'],
  inputs: [
    flowIn(),
    input('color', 'Color', 'color', 0xFF333333),
    input('intensity', 'Intensity', 'f32', 0.3),
  ],
  outputs: [flowOut()],
  compile(ctx) {
    ctx.emit('bridge:setAmbientLight', [ctx.resolveInput('color'), ctx.resolveInput('intensity')], 'void', true);
  },
  execute(ctx) {
    ctx.bridge.call('bridge:setAmbientLight', ctx.getInput('color'), ctx.getInput<number>('intensity'));
  },
};

export const GetAmbientLight: NodeBehaviour = {
  id: 'lighting.getAmbient',
  name: 'Get Ambient Light',
  category: 'Lighting',
  isPure: true,
  color: '#f1c40f',
  icon: 'Cloud',
  description: 'Gets the global ambient light settings.',
  keywords: ['global', 'environment'],
  inputs: [],
  outputs: [
    output('color', 'Color', 'color'),
    output('intensity', 'Intensity', 'f32'),
  ],
  compile(ctx) {
    return ctx.emit('bridge:getAmbientLight', [], 'ambientResult');
  },
  execute(ctx) {
    const r = ctx.bridge.call('bridge:getAmbientLight') as { color: number; intensity: number } | null;
    ctx.setOutput('color', r?.color ?? 0xFF333333);
    ctx.setOutput('intensity', r?.intensity ?? 0.3);
  },
};

export const LIGHTING_NODES: readonly NodeBehaviour[] = [
  SetLightColor, SetLightIntensity, GetLightIntensity,
  SetPointLightRadius, SetPointLightFalloff,
  SetSpotLightAngle, SetSpotLightRange,
  SetDirectionalLightDirection,
  EnableLight, IsLightEnabled, SetLightCastShadows,
  SetAmbientLight, GetAmbientLight,
];
